import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { planService } from '../services/api';
import StripeProductPriceSelector from '../components/StripeProductPriceSelector';
import './Plans.css';

function formatCents(cents, currency = 'EUR') {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(
    Number(cents || 0) / 100
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

function NumericLimitHint({ value }) {
  const isUnlimited = value === null || value === undefined || value === '' || Number(value) === 0;
  return (
    <span className={`feature-limit-hint${isUnlimited ? ' unlimited' : ''}`}>
      {isUnlimited ? 'Illimitato (0 o campo vuoto)' : '0 o campo vuoto = Illimitato'}
    </span>
  );
}

const VERSION_STATUS_LABEL = { DRAFT: 'Draft', PUBLISHED: 'Published', RETIRED: 'Retired' };
const VERSION_STATUS_CLASS = { DRAFT: 'draft', PUBLISHED: 'published', RETIRED: 'retired' };

function formatStripeAmount(amount, currency) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: (currency || 'eur').toUpperCase() }).format(amount / 100);
}

// Inline picker: searches Stripe products live and lets the user pick product + price
function StripeMappingPicker({ priceId, draft, saving, onSelect, onSave, onCancel, saveLabel = 'Salva e verifica', hideSaveButton = false, hideCancel = false }) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [environment, setEnvironment] = useState('');

  const fetchProducts = async (q) => {
    setLoadingProducts(true);
    setFetchError('');
    try {
      const res = await planService.listStripeProducts(q);
      setProducts(res.data);
      setEnvironment(res.environment);
    } catch (err) {
      setFetchError(err?.response?.data?.message || 'Errore nel recupero dei prodotti Stripe');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Auto-load on first open
  React.useEffect(() => { fetchProducts(''); }, []);

  const selectPrice = (product, price) => {
    onSelect({ stripePriceId: price.id, stripeProductId: product.id });
  };

  const canSave = draft.stripePriceId && draft.stripeProductId;

  return (
    <div style={{ marginTop: 10, border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Seleziona prezzo da Stripe</span>
        {environment && (
          <span className={`status-badge ${environment === 'LIVE' ? 'active' : 'draft'}`}>{environment}</span>
        )}
        {!hideCancel && (
          <button className="btn-ghost" style={{ marginLeft: 'auto', fontSize: 12, padding: '3px 10px' }} onClick={onCancel}>
            Annulla
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Filtra per nome prodotto…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchProducts(search)}
        />
        <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => fetchProducts(search)} disabled={loadingProducts}>
          {loadingProducts ? '…' : '🔍'}
        </button>
      </div>

      {fetchError && <p style={{ color: '#dc2626', fontSize: 12, margin: '0 0 8px' }}>{fetchError}</p>}

      {canSave && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#15803d' }}>✓ Selezionato:</span>
          <code style={{ fontSize: 12, background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>{draft.stripePriceId}</code>
          <span style={{ fontSize: 11, color: '#6b7280' }}>{draft.stripeProductId}</span>
          {!hideSaveButton && (
            <button
              className="btn-primary"
              style={{ marginLeft: 'auto', fontSize: 12, padding: '5px 14px' }}
              disabled={saving === `mapping-new-${priceId}`}
              onClick={onSave}
            >
              {saving === `mapping-new-${priceId}` ? '…' : saveLabel}
            </button>
          )}
        </div>
      )}

      {products !== null && (
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
          {products.length === 0 ? (
            <p style={{ padding: '12px', fontSize: 13, color: '#6b7280', margin: 0 }}>Nessun prodotto trovato.</p>
          ) : (
            products.map((product) => (
              <div key={product.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ padding: '8px 12px', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{product.name}</span>
                  <code style={{ fontSize: 11, color: '#9ca3af' }}>{product.id}</code>
                </div>
                {product.prices.length === 0 ? (
                  <p style={{ padding: '6px 12px 8px', fontSize: 12, color: '#9ca3af', margin: 0 }}>Nessun prezzo attivo</p>
                ) : (
                  product.prices.map((price) => {
                    const isSelected = draft.stripePriceId === price.id;
                    return (
                      <div
                        key={price.id}
                        onClick={() => selectPrice(product, price)}
                        style={{
                          padding: '7px 12px 7px 24px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          background: isSelected ? '#eff6ff' : 'transparent',
                          borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}>
                          {formatStripeAmount(price.unit_amount, price.currency)}
                        </span>
                        {price.recurring && (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>
                            / {price.recurring.interval_count > 1 ? price.recurring.interval_count + ' ' : ''}{price.recurring.interval}
                          </span>
                        )}
                        {!price.recurring && <span style={{ fontSize: 11, color: '#6b7280' }}>una tantum</span>}
                        {price.nickname && <span style={{ fontSize: 11, color: '#9ca3af' }}>— {price.nickname}</span>}
                        <code style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{price.id}</code>
                      </div>
                    );
                  })
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function StripeProductGroup({
  group,
  productName,
  saving,
  mappingFormOpen,
  mappingDrafts,
  onTogglePrice,
  onVerifyMapping,
  onDeleteMapping,
  openMappingForm,
  setMappingDrafts,
  onCreateMapping,
  setMappingFormOpen,
  onSync,
  onReplace,
}) {
  const purposes = Array.from(new Set(group.prices.map((price) => price.purpose)));
  const roleLabel = purposes.length === 1
    ? purposes[0] === 'ACTIVATION' ? 'Attivazione' : 'Canone ricorrente'
    : 'Canone + attivazione';
  const hasUnverifiedMapping = group.prices.some((price) =>
    !(price.stripeMappings || []).some((mapping) => mapping.verificationStatus === 'VERIFIED' && !mapping.archivedAt)
  );

  return (
    <section className="stripe-product-group">
      <header className="stripe-product-group-header">
        <div className="stripe-product-identity">
          <div className="stripe-product-title-row">
            <h4>{productName || 'Prodotto Stripe non identificato'}</h4>
            <span className="stripe-role-badge">{roleLabel}</span>
            {hasUnverifiedMapping
              ? <span className="status-badge retired">Configurazione incompleta</span>
              : <span className="status-badge published">Collegato</span>}
          </div>
          <code>{group.productId || 'Nessun Product ID'}</code>
        </div>
        {group.productId && (
          <div className="stripe-product-actions">
            <button className="btn-ghost" disabled={saving} onClick={onSync} title="Importa i prezzi attivi aggiornati di questo prodotto">
              ↻ Sincronizza prezzi
            </button>
            <button className="btn-secondary" disabled={saving} onClick={onReplace} title="Sostituisce solo il prodotto con lo stesso ruolo economico">
              ⇄ Sostituisci prodotto
            </button>
          </div>
        )}
      </header>

      <div className="stripe-product-prices">
        {group.prices.map((price) => (
          <div className={`stripe-product-price ${price.archivedAt ? 'is-archived' : ''}`} key={price.id}>
            <div>
              <strong>{formatCents(price.amountCents, price.currency)}</strong>
              <span>{price.priceType === 'RECURRING' ? `ogni ${price.intervalCount || 1} ${price.interval || 'month'}` : 'una tantum'}</span>
            </div>
            <span className={`status-badge ${price.archivedAt ? 'archived' : 'enabled'}`}>
              {price.archivedAt ? 'Archiviato' : 'Attivo'}
            </span>
            <button
              className={price.archivedAt ? 'btn-secondary' : 'btn-danger'}
              onClick={() => onTogglePrice(price.id, 'archive', !price.archivedAt)}
            >
              {price.archivedAt ? 'Riattiva' : 'Archivia'}
            </button>
          </div>
        ))}
      </div>

      <details className="stripe-technical-details">
        <summary>Dettagli Stripe e mapping</summary>
        {group.prices.map((price) => (
          <div className="stripe-mapping-price" key={`${price.id}-technical`}>
            <div className="stripe-mapping-price-label">
              <strong>{formatCents(price.amountCents, price.currency)}</strong>
              <span>{price.priceType}</span>
            </div>
            {(price.stripeMappings || []).map((mapping) => (
              <div className="stripe-mapping-line" key={mapping.id}>
                <code>{mapping.stripePriceId}</code>
                <span className={`status-badge ${mapping.environment === 'LIVE' ? 'active' : 'draft'}`}>{mapping.environment}</span>
                <span className={`status-badge ${mapping.verificationStatus === 'VERIFIED' ? 'published' : mapping.verificationStatus === 'MISMATCH' ? 'retired' : 'draft'}`}>
                  {mapping.verificationStatus}
                </span>
                <div className="stripe-mapping-actions">
                  <button className="btn-publish" disabled={saving === `mapping-verify-${mapping.id}`} onClick={() => onVerifyMapping(mapping.id)}>
                    {saving === `mapping-verify-${mapping.id}` ? '…' : '↺ Verifica'}
                  </button>
                  <button className="btn-danger" disabled={saving === `mapping-del-${mapping.id}`} onClick={() => onDeleteMapping(mapping.id)}>Elimina</button>
                </div>
              </div>
            ))}
            {!(price.stripeMappings || []).length && <span className="status-badge disabled">Non configurato — checkout bloccato</span>}
            {!mappingFormOpen[price.id] ? (
              <button className="btn-secondary stripe-add-mapping" onClick={() => openMappingForm(price.id)}>+ Collega singolo Price</button>
            ) : (
              <StripeMappingPicker
                priceId={price.id}
                draft={mappingDrafts[price.id] || {}}
                saving={saving}
                onSelect={(draft) => setMappingDrafts((previous) => ({ ...previous, [price.id]: draft }))}
                onSave={() => onCreateMapping(price.id)}
                onCancel={() => setMappingFormOpen((previous) => ({ ...previous, [price.id]: false }))}
              />
            )}
          </div>
        ))}
      </details>
    </section>
  );
}

// Self-contained wizard: creates Plan + v1 PUBLISHED + prices + mappings in one shot
function QuickCreatePlanForm({ saving, onSave, onCancel, featureDefinitions }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [feePercent, setFeePercent] = useState(20);
  const [feeFixedCents, setFeeFixedCents] = useState(0);
  const [trialDays, setTrialDays] = useState(0);
  const [selectedPriceIds, setSelectedPriceIds] = useState([]);
  const [selectedPrices, setSelectedPrices] = useState([]);
  const [productError, setProductError] = useState('');
  const [featDraft, setFeatDraft] = useState({});

  const setFeatField = (defId, value) => setFeatDraft((p) => ({ ...p, [defId]: value }));

  const handlePriceSelection = (priceIds, prices) => {
    const roleOwners = new Map();
    for (const price of prices) {
      const role = price.recurring ? 'SUBSCRIPTION' : 'ACTIVATION';
      const owner = roleOwners.get(role);
      if (owner && owner !== price.stripeProductId) {
        const label = role === 'SUBSCRIPTION' ? 'Canone ricorrente' : 'Attivazione';
        setProductError(`Ruolo già configurato: ${label}. I Price dello stesso ruolo devono appartenere a un solo Product Stripe.`);
        return;
      }
      roleOwners.set(role, price.stripeProductId);
    }
    setSelectedPriceIds(priceIds);
    setSelectedPrices(prices);
    setProductError('');
  };

  const selectedProductIds = Array.from(new Set(selectedPrices.map((price) => price.stripeProductId)));
  const canSubmit = code.trim() && name.trim() && selectedPrices.some((price) => price.recurring);

  const handleSave = () => {
    const features = Object.entries(featDraft)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([featureDefinitionId, value]) => ({ featureDefinitionId, value }));
    onSave({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
      feePercent,
      feeFixedCents,
      trialDays,
      stripeProductIds: selectedProductIds,
      stripePriceIds: selectedPriceIds,
      features,
    });
  };

  const grouped = featureDefinitions.reduce((acc, def) => {
    (acc[def.category] = acc[def.category] || []).push(def);
    return acc;
  }, {});

  return (
    <div className="create-plan-panel">
      <h3>Crea nuovo Piano</h3>
      <div className="form-row" style={{ marginBottom: 16 }}>
        <div className="form-field">
          <label>Code</label>
          <input placeholder="es. STARTER" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required />
        </div>
        <div className="form-field">
          <label>Nome</label>
          <input placeholder="Nome visualizzato" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-field">
          <label>Descrizione <span style={{ fontWeight: 400, color: '#9ca3af' }}>(opzionale)</span></label>
          <input placeholder="Breve descrizione del piano visibile all'admin" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 6px' }}>
          📦 Prodotti Stripe <span style={{ color: '#dc2626' }}>*</span>
          <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8, fontSize: 12 }}>
            i prezzi ricorrenti diventano SUBSCRIPTION, una tantum ACTIVATION
          </span>
        </p>

        {productError && (
          <p style={{ margin: '6px 0', color: '#b91c1c', fontSize: 12, fontWeight: 600 }}>{productError}</p>
        )}
        <StripeProductPriceSelector
          mode="products"
          selectedPriceIds={selectedPriceIds}
          onPriceChange={handlePriceSelection}
          disabled={saving === 'create-plan'}
          maxHeight={320}
        />
      </div>

      <div style={{ marginBottom: 16, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
        <div className="economic-subsection">
          <p className="prices-section-title" style={{ marginBottom: 10 }}>Condizioni economiche — Fee SoonTip</p>
          <div className="feature-category">
            <div className="feature-row">
            <label className="feature-label">Fee % SoonTip</label>
            <div className="feature-input-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="range" min="0" max="100" step="1"
                value={feePercent}
                onChange={(e) => setFeePercent(parseInt(e.target.value, 10))}
                style={{ width: 120 }}
              />
              <input
                type="number" min="0" max="100" step="1"
                className="feature-input percent-number"
                value={feePercent}
                onChange={(e) => setFeePercent(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
              />
              <span className="percent-symbol">%</span>
            </div>
            </div>
            <div className="feature-row">
              <label className="feature-label">Fee fissa (centesimi €)</label>
              <div className="feature-input-wrap">
                <input
                  type="number" min="0" step="1"
                  className="feature-input"
                  value={feeFixedCents}
                  placeholder="0"
                  onChange={(e) => setFeeFixedCents(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                  style={{ width: 80 }}
                />
                <span style={{ marginLeft: 4, fontSize: 12, color: '#6b7280' }}>
                  = EUR {(feeFixedCents / 100).toFixed(2).replace('.', ',')}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="economic-subsection economic-subsection-separated">
          <p className="prices-section-title" style={{ marginBottom: 10 }}>Condizioni economiche — Periodo di prova</p>
          <div className="feature-category">
            <div className="feature-row">
              <label className="feature-label">Prova gratuita (giorni)</label>
              <div className="feature-input-wrap">
                <input
                  type="number" min="0" max="365" step="1"
                  className="feature-input"
                  value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                  style={{ width: 80 }}
                />
                <span style={{ marginLeft: 4, fontSize: 12, color: '#6b7280' }}>0 = nessuna prova</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {featureDefinitions.length > 0 && (
        <div style={{ marginBottom: 16, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
          <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 10px' }}>⚙️ Features &amp; Limiti</p>
          {Object.entries(grouped).map(([category, defs]) => (
            <div key={category} className="feature-category">
              <p className="feature-category-label">{category}</p>
              {defs.map((def) => {
                const vt = (def.valueType || '').toLowerCase();
                const raw = featDraft[def.id];
                const jsonProps = vt === 'json' && def.validationSchema?.properties
                  ? Object.entries(def.validationSchema.properties)
                  : null;
                return (
                  <div key={def.id} className={`feature-row${jsonProps ? ' feature-row-json' : ''}`}>
                    <label className="feature-label" title={def.description || ''}>
                      {def.name}
                      {def.requiredForPublished && <span className="required-dot" title="Obbligatoria">*</span>}
                    </label>
                    {vt === 'boolean' ? (
                      <label className="feature-toggle">
                        <input type="checkbox" checked={raw === true} onChange={(e) => setFeatField(def.id, e.target.checked)} />
                        <span className="feature-toggle-track" />
                      </label>
                    ) : vt === 'integer' ? (
                      <div className="feature-input-wrap">
                        <input type="number" step="1" min="0" className="feature-input" value={raw ?? ''} placeholder="Illimitato"
                          onChange={(e) => setFeatField(def.id, e.target.value === '' ? null : parseInt(e.target.value, 10))} />
                        <NumericLimitHint value={raw} />
                      </div>
                    ) : vt === 'float' ? (
                      <div className="feature-input-wrap">
                        <input type="number" step="0.01" min="0" className="feature-input" value={raw ?? ''} placeholder="Illimitato"
                          onChange={(e) => setFeatField(def.id, e.target.value === '' ? null : parseFloat(e.target.value))} />
                        <NumericLimitHint value={raw} />
                      </div>
                    ) : jsonProps ? (
                      <div className="json-fields">
                        {jsonProps.map(([propKey, propSchema]) => {
                          const obj = (raw && typeof raw === 'object') ? raw : {};
                          const propDefault = propSchema.default ?? null;
                          const propVal = obj[propKey] ?? propDefault ?? '';
                          const isNum = propSchema.type === 'number' || propSchema.type === 'integer';
                          const isPercent = isNum && propSchema.maximum != null && propSchema.maximum <= 100 && (propSchema.minimum ?? 0) === 0;
                          const numVal = propVal === '' ? 0 : Number(propVal);
                          const update = (v) => setFeatField(def.id, { ...obj, [propKey]: v });
                          return (
                            <div key={propKey} className="json-field-row">
                              <span className="json-field-key">{propKey}</span>
                              {isPercent ? (
                                <div className="percent-field">
                                  <input type="range" min={propSchema.minimum ?? 0} max={propSchema.maximum} step="1" value={numVal} onChange={(e) => update(parseFloat(e.target.value))} />
                                  <input type="number" min={propSchema.minimum ?? 0} max={propSchema.maximum} step="1" className="feature-input percent-number" value={propVal === '' ? '' : propVal} placeholder="0" onChange={(e) => update(e.target.value === '' ? 0 : parseFloat(e.target.value))} />
                                  <span className="percent-symbol">%</span>
                                </div>
                              ) : (
                                <input type={isNum ? 'number' : 'text'} step={propSchema.type === 'integer' ? '1' : propSchema.type === 'number' ? '0.01' : undefined}
                                  min={propSchema.minimum ?? (isNum ? '0' : undefined)} max={propSchema.maximum} className="feature-input"
                                  value={propVal} placeholder={propDefault != null ? String(propDefault) : (isNum ? '0' : '—')}
                                  onChange={(e) => {
                                    const v = isNum ? (e.target.value === '' ? (propDefault ?? 0) : propSchema.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value)) : e.target.value || (propDefault ?? null);
                                    update(v);
                                  }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <input type="text" className="feature-input" value={raw ?? ''} placeholder="—"
                        onChange={(e) => setFeatField(def.id, e.target.value || null)} />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn-primary" disabled={!canSubmit || saving === 'create-plan'} onClick={handleSave}>
          {saving === 'create-plan' ? 'Creazione…' : '🚀 Crea e attiva piano'}
        </button>
        <button className="btn-ghost" onClick={onCancel}>Annulla</button>
        {!canSubmit && <span style={{ fontSize: 12, color: '#9ca3af' }}>Seleziona almeno un prodotto Stripe per continuare</span>}
      </div>
    </div>
  );
}

const Plans = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [hideInactivePlans, setHideInactivePlans] = useState(true);

  const [showCreate, setShowCreate] = useState(false);

  const [planEditing, setPlanEditing] = useState({});
  const [planDrafts, setPlanDrafts] = useState({});

  const [expandedVersions, setExpandedVersions] = useState({});

  const [mappingDrafts, setMappingDrafts] = useState({});
  const [mappingFormOpen, setMappingFormOpen] = useState({});
  const [showArchivedPrices, setShowArchivedPrices] = useState({});

  const [featureDefinitions, setFeatureDefinitions] = useState([]);
  const [versionFeeDrafts, setVersionFeeDrafts] = useState({});
  const [planFeatureEditOpen, setPlanFeatureEditOpen] = useState({});
  const [planFeatureDrafts, setPlanFeatureDrafts] = useState({});
  const [planFeatureSaveResult, setPlanFeatureSaveResult] = useState({});
  const [productPickerOpen, setProductPickerOpen] = useState({});
  const [versionOperationErrors, setVersionOperationErrors] = useState({});
  const [stripeProductNames, setStripeProductNames] = useState({});
  const [expandedHistoryVersion, setExpandedHistoryVersion] = useState(null);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || new Date(b.updatedAt) - new Date(a.updatedAt)),
    [plans]
  );
  const visiblePlans = useMemo(
    () => hideInactivePlans ? sortedPlans.filter((plan) => plan.active) : sortedPlans,
    [hideInactivePlans, sortedPlans]
  );
  const inactivePlansCount = sortedPlans.length - sortedPlans.filter((plan) => plan.active).length;

  // ── Plan order configuration ──────────────────────────────────
  const [showReorder, setShowReorder] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [savingReorder, setSavingReorder] = useState(false);

  const openReorderModal = () => {
    setReorderItems(sortedPlans.map((p, idx) => ({ planId: p.id, name: p.name, code: p.code, active: p.active, displayOrder: p.displayOrder ?? idx })));
    setShowReorder(true);
  };

  const moveReorderItem = (index, direction) => {
    setReorderItems((items) => {
      const next = [...items];
      const swapIdx = index + direction;
      if (swapIdx < 0 || swapIdx >= next.length) return items;
      [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
      return next.map((item, i) => ({ ...item, displayOrder: i }));
    });
  };

  const moveInactivePlansToBottom = () => {
    setReorderItems((items) => [
      ...items.filter((item) => item.active),
      ...items.filter((item) => !item.active),
    ].map((item, index) => ({ ...item, displayOrder: index })));
  };

  const saveReorder = async () => {
    setSavingReorder(true);
    try {
      await planService.reorderPlans(reorderItems.map(({ planId, displayOrder }) => ({ planId, displayOrder })));
      await loadPlans();
      setShowReorder(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Errore nel salvataggio ordine');
    } finally {
      setSavingReorder(false);
    }
  };

  const loadPlans = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await planService.list();
      setPlans(res?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Errore nel caricamento dei piani');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlans(); }, []);

  useEffect(() => {
    planService.listFeatureDefinitions()
      .then((res) => setFeatureDefinitions(res?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    planService.listStripeProducts('')
      .then((res) => {
        const map = {};
        (res?.data || []).forEach((p) => { map[p.id] = p.name; });
        setStripeProductNames(map);
      })
      .catch(() => {});
  }, []);

  const withSaving = async (key, fn) => {
    setSaving(key);
    setError('');
    try {
      await fn();
      await loadPlans();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Errore');
    } finally {
      setSaving('');
    }
  };

  const onQuickCreatePlan = async (formData) =>
    withSaving('create-plan', async () => {
      await planService.quickCreate(formData);
      setShowCreate(false);
    });

  const onSavePlan = (plan) =>
    withSaving(`plan-${plan.id}`, async () => {
      const draft = planDrafts[plan.id] || {};
      await planService.update(plan.id, {
        name: draft.name ?? plan.name,
        active: draft.active ?? plan.active,
      });
      setPlanEditing((p) => ({ ...p, [plan.id]: false }));
    });

  const onUpdateVersion = (versionId, payload) =>
    withSaving(`version-${versionId}`, () => planService.updateVersion(versionId, payload));

  const onDeleteVersion = (versionId) => {
    if (!window.confirm('Eliminare questa versione? L\'operazione è irreversibile.')) return;
    withSaving(`version-${versionId}`, () => planService.deleteVersion(versionId));
  };

  const onDeletePlan = (plan) => {
    if (!window.confirm(`Eliminare il piano "${plan.name}" (${plan.code}) e tutte le sue versioni? L'operazione è irreversibile.`)) return;
    withSaving(`plan-${plan.id}`, () => planService.deletePlan(plan.id));
  };

  const onStartEdit = (planId) =>
    withSaving(`start-edit-${planId}`, async () => {
      const res = await planService.startEdit(planId);
      if (res?.data?.id) setExpandedVersions((p) => ({ ...p, [res.data.id]: true }));
    });

  const onImportFromProduct = async (versionId, stripeProductId, mode, targetRoles = [], stripePriceIds = []) => {
    setSaving(`import-product-${versionId}`);
    setVersionOperationErrors((previous) => ({ ...previous, [versionId]: '' }));
    try {
      await planService.importPricesFromProduct(versionId, stripeProductId, mode, targetRoles, stripePriceIds);
      await loadPlans();
      setProductPickerOpen((previous) => ({ ...previous, [versionId]: false }));
    } catch (err) {
      setVersionOperationErrors((previous) => ({
        ...previous,
        [versionId]: err?.response?.data?.error || err?.message || 'Errore durante l’aggiornamento dei prezzi Stripe.',
      }));
    } finally {
      setSaving('');
    }
  };

  const openPlanFeatureEdit = (plan, activeVersion) => {
    if (!planFeatureEditOpen[plan.id]) {
      const existingMap = Object.fromEntries((activeVersion?.features || []).map(f => [f.featureDefinitionId, f.value]));
      // Seed all known feature definitions so toggling off also gets persisted
      const init = {};
      featureDefinitions.forEach(fd => {
        if (Object.prototype.hasOwnProperty.call(existingMap, fd.id)) {
          init[fd.id] = existingMap[fd.id];
        } else if (fd.valueType === 'BOOLEAN') {
          init[fd.id] = false;
        } else if (fd.valueType === 'INTEGER' || fd.valueType === 'FLOAT') {
          init[fd.id] = null;
        } else {
          init[fd.id] = {};
        }
      });
      setPlanFeatureDrafts((p) => ({ ...p, [plan.id]: init }));
      setPlanFeatureSaveResult((p) => ({ ...p, [plan.id]: null }));
    }
    setPlanFeatureEditOpen((p) => ({ ...p, [plan.id]: !p[plan.id] }));
  };

  const onSavePlanFeatures = (planId) =>
    withSaving(`plan-features-${planId}`, async () => {
      const draft = planFeatureDrafts[planId] || {};
      const features = Object.entries(draft).map(([featureDefinitionId, value]) => ({ featureDefinitionId, value }));
      const result = await planService.savePlanFeatures(planId, features);
      setPlanFeatureSaveResult((p) => ({ ...p, [planId]: result?.updatedVersionCount ?? '?' }));
      loadPlans();
    });

  const onTogglePrice = (priceId, field, value) =>
    withSaving(`price-${priceId}`, () => planService.updatePrice(priceId, { [field]: value }));

  const onCreateMapping = (priceId) =>
    withSaving(`mapping-new-${priceId}`, async () => {
      const d = mappingDrafts[priceId] || {};
      await planService.createStripeMapping(priceId, {
        stripePriceId: d.stripePriceId?.trim(),
        stripeProductId: d.stripeProductId?.trim(),
      });
      setMappingDrafts((p) => ({ ...p, [priceId]: {} }));
      setMappingFormOpen((p) => ({ ...p, [priceId]: false }));
    });

  const onVerifyMapping = (mappingId) =>
    withSaving(`mapping-verify-${mappingId}`, () => planService.verifyStripeMapping(mappingId));

  const onDeleteMapping = (mappingId) =>
    withSaving(`mapping-del-${mappingId}`, () => planService.deleteStripeMapping(mappingId));

  const openMappingForm = (priceId) => {
    setMappingFormOpen((p) => ({ ...p, [priceId]: true }));
    setMappingDrafts((p) => ({ ...p, [priceId]: p[priceId] || {} }));
  };

  const toggleVersionExpand = (versionId) =>
    setExpandedVersions((p) => ({ ...p, [versionId]: !p[versionId] }));

  return (
    <div className="plans-page">
      <div className="plans-header">
        <div>
          <h2>Piani &amp; Versioni</h2>
          <p className="subtitle">Gestione del catalogo piani, versioni e prezzi per l'onboarding.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={openReorderModal} title="Configura ordine di visualizzazione dei piani">
            ↕ Ordine piani
          </button>
          <button className="btn-primary" onClick={() => setShowCreate((v) => !v)}>
            {showCreate ? '✕ Annulla' : '+ Nuovo Piano'}
          </button>
        </div>
      </div>

      {/* ── Plan Order Configuration Modal ── */}
      {showReorder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700 }}>↕ Ordine di visualizzazione piani</h3>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b' }}>
              Trascina o usa le frecce per cambiare l'ordine in cui i piani appaiono durante l'onboarding.
            </p>
            {reorderItems.some((item) => !item.active) && (
              <div className="reorder-inactive-tools">
                <span>Gli inattivi non appaiono nell’onboarding.</span>
                <button type="button" onClick={moveInactivePlansToBottom}>
                  Sposta inattivi in fondo
                </button>
              </div>
            )}
            <div className="reorder-plan-list">
              {reorderItems.map((item, idx) => (
                <div key={item.planId} className={`reorder-plan-item ${!item.active ? 'inactive' : ''}`}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', width: 20, textAlign: 'center' }}>{idx + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', background: '#e2e8f0', borderRadius: 4, padding: '1px 5px' }}>{item.code}</span>
                    {!item.active && <span className="reorder-inactive-badge">Inattivo</span>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button
                      onClick={() => moveReorderItem(idx, -1)}
                      disabled={idx === 0}
                      style={{ border: '1px solid #e2e8f0', background: idx === 0 ? '#f1f5f9' : '#fff', borderRadius: 4, padding: '2px 7px', cursor: idx === 0 ? 'default' : 'pointer', fontSize: 12, color: idx === 0 ? '#cbd5e1' : '#334155' }}
                    >▲</button>
                    <button
                      onClick={() => moveReorderItem(idx, 1)}
                      disabled={idx === reorderItems.length - 1}
                      style={{ border: '1px solid #e2e8f0', background: idx === reorderItems.length - 1 ? '#f1f5f9' : '#fff', borderRadius: 4, padding: '2px 7px', cursor: idx === reorderItems.length - 1 ? 'default' : 'pointer', fontSize: 12, color: idx === reorderItems.length - 1 ? '#cbd5e1' : '#334155' }}
                    >▼</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn-secondary" onClick={() => setShowReorder(false)} disabled={savingReorder}>Annulla</button>
              <button className="btn-primary" onClick={saveReorder} disabled={savingReorder}>
                {savingReorder ? 'Salvataggio…' : '✓ Salva ordine'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="plans-error">
          <span>⚠️</span>
          <span>{error}</span>
          <button className="btn-sm-text" onClick={() => setError('')} style={{ marginLeft: 'auto' }}>Chiudi</button>
        </div>
      )}

      {/* ── Guida rapida ─────────────────────────────────────────── */}
      <details style={{ marginBottom: 20, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#334155', userSelect: 'none' }}>
          ℹ️ Guida a Piani, Prodotti Stripe, ruoli, prezzi e mapping
        </summary>
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 18, fontSize: 13, color: '#475569', lineHeight: 1.65 }}>

          <div>
            <strong style={{ fontSize: 13.5 }}>🗺️ Panoramica dell'architettura</strong>
            <p style={{ margin: '6px 0 0' }}>
              Un <strong>Piano SoonTip</strong> è l'offerta commerciale logica (es. "Start" o "Prime") e non contiene direttamente importi.
              Le condizioni economiche vivono nelle sue <strong>Versioni</strong>: una versione è in produzione per i nuovi contratti, mentre una bozza raccoglie le modifiche non ancora pubblicate.
              Dentro ogni versione SoonTip salva i propri <strong>Prezzi</strong> e il relativo <strong>Mapping Stripe</strong>.
            </p>
            <p style={{ margin: '6px 0 0' }}>
              Il <strong>Prodotto Stripe</strong> mostrato nella schermata non è un ulteriore record del database SoonTip: è il gruppo ricostruito tramite
              <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, marginLeft: 4 }}>stripeProductId</code> presente nei mapping.
              Ogni prezzo SoonTip è collegato a uno specifico <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>price_xxx</code> e al suo
              <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, marginLeft: 4 }}>prod_xxx</code>. Stripe resta la fonte dei Price; SoonTip conserva la versione utilizzabile dal checkout e la storia contrattuale.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🏷️ Che cos'è un Ruolo economico SoonTip?</strong>
            <p style={{ margin: '6px 0 4px' }}>
              Il <strong>Ruolo</strong> indica <em>per quale funzione SoonTip usa un prezzo nel piano e nel checkout</em>.
              È un dato del <strong>PlanPrice SoonTip</strong>, salvato nel campo <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>purpose</code>;
              non è un ruolo utente, un permesso, un tipo di abbonamento Stripe o un campo configurato sul Product Stripe.
            </p>
            <p style={{ margin: '6px 0 4px' }}>
              Stripe descrive <em>come</em> viene addebitato un Price, ad esempio ricorrente o una tantum. SoonTip traduce questa caratteristica nella funzione economica che quel prezzo svolge:
            </p>
            <ul style={{ margin: '4px 0 0 16px' }}>
              <li><strong>SUBSCRIPTION — Canone ricorrente:</strong> identifica i PlanPrice usati come canone del piano. Nasce da un Price Stripe <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>recurring</code>. Il checkout richiede almeno un prezzo con questo ruolo e, se esistono più intervalli dello stesso Product (es. mensile e annuale), consente di scegliere il canone applicabile.</li>
              <li><strong>ACTIVATION — Attivazione:</strong> identifica il PlanPrice opzionale addebitato una sola volta all'avvio del contratto. Nasce da un Price Stripe <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>one_time</code>.</li>
            </ul>
            <p style={{ margin: '6px 0 4px' }}>
              Il <strong>mapping non contiene il ruolo</strong>: collega il PlanPrice che possiede quel ruolo al relativo Price e Product Stripe.
              L'interfaccia raggruppa i PlanPrice per <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>stripeProductId</code> e mostra sulla card l'insieme dei loro ruoli.
              Per questo dire che <em>"un Product ha il ruolo Canone"</em> è una forma abbreviata per dire che <em>"quel Product è collegato a uno o più PlanPrice con purpose SUBSCRIPTION"</em>.
            </p>
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f1f5f9', borderRadius: 6, fontSize: 12 }}>
              <strong>Esempi:</strong>
              <ul style={{ margin: '4px 0 0 16px' }}>
                <li>Product "SoonTip Base" con Price mensile e annuale ricorrenti → due PlanPrice, entrambi con ruolo <strong>SUBSCRIPTION</strong>.</li>
                <li>Product "Attivazione SoonTip" con un Price una tantum → un PlanPrice con ruolo <strong>ACTIVATION</strong>.</li>
                <li>Un unico Product con Price ricorrenti e una tantum → la card riassume <strong>Canone + attivazione</strong>, perché i suoi PlanPrice coprono entrambi i ruoli.</li>
              </ul>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
              Il ruolo è anche un <strong>confine di sicurezza</strong>: nella stessa versione ogni ruolo può appartenere a un solo Product Stripe. Tutti i Price <strong>SUBSCRIPTION</strong> devono quindi appartenere allo stesso Product; il Product <strong>ACTIVATION</strong> può essere diverso, perché svolge un altro ruolo economico. Sono ammessi più Price dello stesso Product per lo stesso ruolo, purché non abbiano la stessa periodicità. In questo modo SoonTip sa senza ambiguità quale gruppo usare nel checkout e quale gruppo sostituire o sincronizzare.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🚀 Flusso 1 — Creazione rapida (primo setup)</strong>
            <p style={{ margin: '6px 0 4px' }}>
              Usa il pulsante <strong>"+ Nuovo piano"</strong> in alto a destra. Il form di creazione rapida chiede solo:
            </p>
            <ol style={{ margin: '0 0 0 16px' }}>
              <li>Un <strong>codice</strong> breve e univoco (es. <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>PRIME</code>) — viene convertito in maiuscolo.</li>
              <li>Un <strong>nome visibile</strong> (es. "Prime").</li>
              <li>Uno o più <strong>Price Stripe</strong> — cercali per Product e seleziona gli identificativi esatti da importare. Il checkbox sul Product seleziona in blocco tutti i Price compatibili visibili; i checkbox figli permettono una scelta puntuale.</li>
            </ol>
            <p style={{ margin: '6px 0 0' }}>
              Al salvataggio il sistema crea automaticamente: Piano + Versione v1 (già PUBLISHED e attiva) + Prezzi + Mapping Stripe verificati.
              <strong> Non devi inserire manualmente nessun ID.</strong>
              La selezione viene bloccata prima del salvataggio se due Product condividono lo stesso ruolo economico o se scegli due Price ricorrenti con la stessa periodicità.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>💰 Flusso 2 — Modificare le condizioni economiche (prezzi o fee SoonTip)</strong>
            <p style={{ margin: '6px 0 4px', fontSize: 12, color: '#6b7280' }}>
              Usa questo flusso <strong>solo per cambiare prezzi o la fee SoonTip</strong>. Crea una nuova versione del piano:
              i subscriber esistenti restano sulla versione attuale con tutte le loro condizioni economiche frozen (prezzi, fee, termini contrattuali);
              le nuove condizioni si applicano solo ai nuovi sottoscrittori che firmano dopo la pubblicazione.
            </p>
            <ol style={{ margin: '6px 0 0 16px' }}>
              <li>Clicca <strong>"💰 Modifica condizioni economiche"</strong>: viene creata una <strong>bozza</strong> (DRAFT) con copia di prezzi e fee dalla versione attiva.</li>
              <li>Espandi la bozza e usa le azioni Stripe (tabella sotto) per aggiornare i prezzi, oppure modifica la <strong>fee SoonTip</strong>.</li>
              <li>Clicca <strong>"🚀 Pubblica e attiva"</strong>: la bozza diventa la nuova versione in produzione, quella precedente viene ritirata.</li>
              <li>Per annullare senza pubblicare: <strong>"Scarta bozza"</strong>.</li>
            </ol>
            <table style={{ marginTop: 10, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Azione nella bozza</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Quando usarla</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['+ Aggiungi prodotto Stripe', 'Importa soltanto i singoli Price selezionati. Il checkbox Product seleziona in blocco tutti i Price compatibili visibili; gli altri Product non vengono modificati.'],
                  ['⇄ Sostituisci prodotto', 'Richiede un Product con gli stessi ruoli di quello sostituito. Archivia i prezzi di quei ruoli e importa soltanto i Price selezionati del nuovo Product; gli altri ruoli restano invariati.'],
                  ['↻ Sincronizza prezzi', 'È intenzionalmente Product-wide: mantiene i Price ancora attivi, importa tutti i nuovi Price attivi del Product, archivia quelli non più attivi su Stripe e aggiorna verifica e snapshot.'],
                  ['Collega singolo Price', 'Operazione tecnica di recupero per creare manualmente un mapping; normalmente si usa il picker del Product.'],
                  ['Archivia', 'Esclude manualmente un prezzo dalle nuove selezioni e dai nuovi contratti, senza cancellare record, mapping o storia.'],
                  ['Riattiva', 'Rende nuovamente selezionabile un prezzo archiviato nella stessa versione.'],
                ].map(([azione, desc]) => (
                  <tr key={azione}>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11 }}>{azione}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#92400e', background: '#fffbeb', borderRadius: 6, padding: '6px 10px' }}>
              ⚠️ Tutte le operazioni economiche avvengono nella bozza. "Pubblica e attiva" è disabilitato se importi, mapping, prezzi, fee e trial sono identici alla versione in produzione. Per aggiungere o migliorare feature usa ⚙️ Gestisci feature, non questo flusso.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>⬆️ Upgrade di un tenant già attivo</strong>
            <p style={{ margin: '6px 0 4px', fontSize: 12, color: '#6b7280' }}>
              L'upgrade non sposta automaticamente i subscriber quando pubblichi una nuova versione. Il tenant sceglie un piano dal Dashboard, rivede un nuovo Allegato contrattuale e firma tramite OTP; SoonTip aggiorna Stripe e rende effettiva la nuova versione solo dopo la conferma del provider.
            </p>
            <ul style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#6b7280' }}>
              <li><strong>Gerarchia generale corrente:</strong> il target deve essere pubblicato, attivo per nuove sottoscrizioni e avere un importo ricorrente nominale superiore a quello effettivo corrente. Il backend ripete il controllo sia in anteprima sia alla firma.</li>
              <li><strong>Piano standard:</strong> l'amendment sostituisce prezzo, fee, limiti e funzionalità con lo snapshot standard della versione scelta.</li>
              <li><strong>Contratto con condizioni personalizzate:</strong> eventuali override ancora effettivi su canone, fee, limiti o feature non vengono migrati. Il Dashboard mostra un avviso contestuale e l'upgrade passa alle condizioni standard del target.</li>
              <li><strong>Attivazione:</strong> il costo una tantum non viene addebitato nuovamente durante l'upgrade.</li>
              <li><strong>CTA contestuali:</strong> un suggerimento nato da un limite o da una funzionalità compare solo se il piano target migliora realmente quella specifica capacità, oltre a rispettare i requisiti economici e di disponibilità.</li>
            </ul>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#1e40af', background: '#eff6ff', borderRadius: 6, padding: '6px 10px' }}>
              <strong>Evoluzione prevista:</strong> introdurre un ordine commerciale esplicito tra piani e un flusso Admin per migrare selettivamente gli override o generare una nuova offerta personalizzata. Fino ad allora la gerarchia generale confronta gli importi nominali, anche tra periodicità diverse, e gli override non vengono trasferiti automaticamente.
            </p>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>⚙️ Flusso 3 — Aggiungere o migliorare feature (migliorativo)</strong>
            <p style={{ margin: '6px 0 4px', fontSize: 12, color: '#6b7280' }}>
              Usa <strong>⚙️ Gestisci feature</strong> nel header del piano per aggiungere nuove funzionalità o alzare limiti.
              La modifica aggiorna le <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>PlanFeature</code> di tutte le versioni del piano, ma il suo effetto sui subscriber esistenti dipende dallo snapshot:
            </p>
            <ul style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#6b7280' }}>
              <li><strong>Feature nuova (non presente nello snapshot al momento della firma)</strong>: il valore viene propagato immediatamente a tutti i subscriber, inclusi quelli con contratto già firmato — lo snapshot non la copre, quindi vince il valore base. ✅ Il caso tipico "ricevono qualcosa in più".</li>
              <li><strong>Feature già presente nello snapshot</strong>: cambia solo per i nuovi iscritti. Chi ha già firmato continua a vedere il valore fotografato nello snapshot — lo snapshot sovrascrive sempre il valore base. ⚠️ Per aggiornare quella feature per chi ha già firmato serve un amendment esplicito.</li>
            </ul>
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f1f5f9', borderRadius: 6, fontSize: 12 }}>
              <strong>Limite percettori — MAX_RECIPIENTS_PER_VENUE:</strong>
              <ul style={{ margin: '4px 0 0 16px' }}>
                <li>Il valore è applicato separatamente a ogni locale e conta i Worker con <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>active = true</code>.</li>
                <li>La creazione di un nuovo percettore e la riattivazione di uno disattivato vengono bloccate quando il locale ha raggiunto il limite.</li>
                <li>Invio o reinvio dell'invito, assegnazioni ai team e modifica dei dati non consumano un posto aggiuntivo.</li>
                <li>Un limite assente o pari a 0 significa illimitato. Se una modifica contrattuale abbassa il limite sotto l'utilizzo corrente, SoonTip non disattiva automaticamente nessuno, ma blocca nuove creazioni e riattivazioni finché l'utilizzo non rientra nella soglia.</li>
                <li>Per contratti firmati viene usato il valore effettivo dello snapshot, inclusi eventuali override commerciali, non semplicemente il valore corrente del piano base.</li>
              </ul>
            </div>
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f1f5f9', borderRadius: 6, fontSize: 12 }}>
              <strong>Gestione dispositivi — DEVICES_ENABLED:</strong>
              <ul style={{ margin: '4px 0 0 16px' }}>
                <li>Abilita provisioning, configurazione, assegnazione e riattivazione dei dispositivi fisici SoonTip. È indipendente da <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>TIP_LINKS_ENABLED</code>, quindi un piano può offrire QR e Tip Link senza dispositivi.</li>
                <li>Quando è disabilitata, la tab Devices resta visibile in modalità bloccata e propone Billing solo se esiste un piano superiore che abilita realmente la feature.</li>
                <li>I device preesistenti non vengono disabilitati automaticamente: restano consultabili e possono essere disabilitati o eliminati, ma non modificati o riattivati.</li>
                <li>Il limite <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>MAX_DEVICES_PER_VENUE</code> viene applicato e mostrato soltanto quando la gestione dispositivi è abilitata.</li>
                <li>Valore assente o <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>false</code> significa funzionalità non inclusa. Snapshot firmati e override commerciali hanno precedenza sul valore base del piano.</li>
              </ul>
            </div>
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f1f5f9', borderRadius: 6, fontSize: 12 }}>
              <strong>Limite dispositivi — MAX_DEVICES_PER_VENUE:</strong>
              <ul style={{ margin: '4px 0 0 16px' }}>
                <li>Il valore è applicato separatamente a ogni locale e conta i device in stato <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>PROVISIONED</code>, <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>SHIPPED_PENDING_ACTIVATION</code> o <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>ACTIVE</code>.</li>
                <li>I device <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>DISABLED</code> non consumano capacità. La creazione e il passaggio da DISABLED a uno stato operativo vengono bloccati quando il locale ha raggiunto il limite.</li>
                <li>Modificare etichetta, area o assegnazione non consuma un posto aggiuntivo.</li>
                <li>Un limite assente o pari a 0 significa illimitato. Se il locale è già oltre soglia, SoonTip non disabilita automaticamente alcun device, ma blocca nuove configurazioni e riabilitazioni.</li>
                <li>Il dashboard mostra utilizzo e disponibilità prima del provisioning e propone l'upgrade solo se un piano pubblicato, acquistabile e più costoso offre un limite maggiore o illimitato.</li>
                <li>Per contratti firmati viene usato il valore effettivo dello snapshot, inclusi eventuali override commerciali.</li>
              </ul>
            </div>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🔗 Product, Price e Mapping Stripe — come funzionano davvero</strong>
            <p style={{ margin: '6px 0 4px' }}>
              Stripe separa il contenitore commerciale (<strong>Product</strong>) dalla singola condizione economica immutabile (<strong>Price</strong>).
              SoonTip importa ciascun Price come un proprio <strong>PlanPrice</strong> e crea un mapping che conserva insieme Product ID, Price ID, ambiente, stato di verifica e snapshot Stripe.
              Il mapping serve quindi sia al checkout sia a ricostruire correttamente le card Product. Il ruolo rimane invece sul PlanPrice: il mapping indica <em>a quali oggetti Stripe</em> quel prezzo SoonTip è collegato, non <em>come SoonTip lo usa</em>.
            </p>
            <ul style={{ margin: '4px 0 10px 16px' }}>
              <li><strong>Product ID:</strong> identifica il gruppo Stripe e delimita la sincronizzazione. Sincronizzare un Product non può archiviare prezzi collegati a un Product ID diverso.</li>
              <li><strong>Price ID:</strong> è l'identificativo esatto usato da Stripe per l'addebito. Importo, valuta, ricorrenza e intervallo provengono da questo oggetto.</li>
              <li><strong>Ambiente:</strong> TEST e LIVE hanno mapping separati; il server seleziona automaticamente quello coerente con l'ambiente in uso.</li>
              <li><strong>Snapshot:</strong> conserva i dati Stripe letti durante importazione o sincronizzazione e viene aggiornato quando si sincronizza il Product.</li>
            </ul>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Stato verifica</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Cosa significa</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Checkout?</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['VERIFIED', 'Price trovato su Stripe e appartenente al Product dichiarato dal mapping.', '✅'],
                  ['MISMATCH', 'Price esistente, ma associato a un Product Stripe diverso da quello salvato.', '❌'],
                  ['UNVERIFIED', 'Mapping non ancora verificato oppure verifica Stripe non completata.', '❌'],
                ].map(([stato, desc, checkout]) => (
                  <tr key={stato}>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>{stato}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>{desc}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{checkout}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul style={{ margin: '8px 0 0 16px' }}>
              <li>Importazione e sincronizzazione creano o aggiornano automaticamente mapping verificati: normalmente non devi inserire ID a mano.</li>
              <li>Usa <strong>"↺ Verifica"</strong> nei dettagli tecnici per ricontrollare un mapping manuale o diagnosticare una configurazione precedente.</li>
              <li>Per correggere un mapping errato: apri <strong>Dettagli tecnici</strong>, elimina il mapping e usa <strong>"Collega singolo Price"</strong>, oppure sostituisci/sincronizza il Product quando l'intero gruppo deve essere riallineato.</li>
            </ul>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>🛡️ Archiviazione e protezioni</strong>
            <p style={{ margin: '6px 0 4px' }}>
              <strong>Archiviare non significa eliminare.</strong> Il record rimane nel database con mapping e riferimenti storici; semplicemente non è più utilizzabile per nuove sottoscrizioni. Questo consente di cambiare il catalogo senza alterare contratti e condizioni già firmate.
            </p>
            <ul style={{ margin: '4px 0 0 16px' }}>
              <li><strong>Sincronizzazione:</strong> archivia solo i Price del Product selezionato che Stripe non restituisce più come attivi. Gli altri Product ID sono esclusi esplicitamente.</li>
              <li><strong>Sostituzione:</strong> archivia solo i prezzi dei ruoli sostituiti. Il nuovo Product deve possedere esattamente gli stessi ruoli.</li>
              <li><strong>Pubblicazione:</strong> la bozza diventa la nuova versione attiva; i prezzi delle versioni precedenti vengono archiviati, ma restano disponibili per storia e riferimenti contrattuali.</li>
              <li><strong>Modalità esplicita:</strong> il backend rifiuta importazioni senza operazione valida; non esiste un comportamento predefinito che possa sostituire accidentalmente tutto il piano.</li>
              <li><strong>Transazione:</strong> archiviazione, importazione e mapping avvengono insieme. Se un passaggio fallisce, l'operazione viene annullata senza lasciare una configurazione parziale.</li>
            </ul>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>📋 Versioni — stati possibili</strong>
            <table style={{ marginTop: 6, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Stato</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Cosa significa</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['DRAFT (bozza)', 'In preparazione. Non attiva per i nuovi iscritti. Può esistere solo una bozza per piano.'],
                  ['PUBLISHED — attiva', 'La versione "In produzione": quella che viene usata per i nuovi contratti.'],
                  ['PUBLISHED — ritirata', 'Pubblicata in passato ma ora sostituita. I contratti esistenti la usano ancora.'],
                  ['RETIRED', 'Ritirata esplicitamente. I contratti in corso continuano, nessuno nuovo.'],
                ].map(([stato, desc]) => (
                  <tr key={stato}>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600, whiteSpace: 'nowrap' }}>{stato}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <strong style={{ fontSize: 13.5 }}>⚙️ Quando usare "Gestisci feature" vs creare una nuova versione vs un nuovo piano</strong>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#7c2d12', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, padding: '8px 10px', lineHeight: 1.5 }}>
              <strong>Passaggio obbligatorio per una nuova feature:</strong> creare la <code>FeatureDefinition</code> la rende disponibile nel catalogo Admin, ma non la assegna automaticamente a nessun piano. Per renderla effettiva devi aprire <strong>Gestisci feature</strong>, impostare il valore e premere <strong>Salva feature</strong>. Il salvataggio crea il <code>PlanFeature</code> su tutte le versioni del piano. Se la feature non esisteva nei vecchi snapshot firmati, il nuovo valore base si applica immediatamente anche ai subscriber esistenti; verifica quindi l'impatto prima di salvare.
            </p>
            <table style={{ marginTop: 6, borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Operazione</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Strumento</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Impatto su subscriber esistenti</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Aggiungere una feature mai esistita prima (nuovo toggle, nuovo campo)', '⚙️ Gestisci feature', 'Immediato per tutti — la feature non era nello snapshot, quindi vince il valore base aggiornato.'],
                  ['Modificare una feature già presente nei contratti firmati', '⚙️ Gestisci feature', 'Solo nuovi iscritti. Chi ha già firmato ha quella feature "fotografata" nello snapshot alla firma — lo snapshot sovrascrive il valore base.'],
                  ['Cambiare prezzo o condizioni economiche', '💰 Modifica condizioni economiche → nuova versione', 'Nessuno — i subscriber esistenti restano sulla versione vecchia finché non firmi un amendment.'],
                  ['Rimuovere una feature definitivamente (non vuoi rischiare che ritorni in futuro)', 'Crea un nuovo piano ex novo → disattiva il vecchio', 'I subscriber esistenti restano sul vecchio piano frozen. Nuovi iscritti vanno sul nuovo. Usa amendments per migrare chi vuoi.'],
                ].map(([op, strumento, impatto]) => (
                  <tr key={op}>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0' }}>{op}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', fontWeight: 600, whiteSpace: 'nowrap' }}>{strumento}</td>
                    <td style={{ padding: '5px 8px', border: '1px solid #e2e8f0', color: '#6b7280' }}>{impatto}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#92400e', background: '#fffbeb', borderRadius: 6, padding: '6px 10px' }}>
              🔒 <strong>Come funziona lo snapshot:</strong> alla firma di un contratto le feature attive vengono fotografate nel <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>resolvedPlanSnapshot</code>.
              Quando il sistema calcola i permessi di un tenant, legge prima i valori base (<code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>PlanFeature</code>) e poi sovrascrive con lo snapshot — lo snapshot vince sempre.
              Per una feature già presente nello snapshot, modificare il valore via "Gestisci feature" non cambia il contratto firmato e serve un amendment esplicito. Una feature completamente nuova, invece, non ha una chiave nello snapshot che possa sovrascriverla: dopo "Salva feature" il valore base si applica anche ai contratti esistenti.
            </p>
          </div>

        </div>
      </details>

      {showCreate && (
        <QuickCreatePlanForm
          saving={saving}
          onSave={onQuickCreatePlan}
          featureDefinitions={featureDefinitions}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {!loading && sortedPlans.length > 0 && (
        <div className="plans-filter-bar">
          <div>
            <strong>Visibilità piani</strong>
            <span>
              {hideInactivePlans && inactivePlansCount > 0
                ? `${inactivePlansCount} ${inactivePlansCount === 1 ? 'piano inattivo nascosto' : 'piani inattivi nascosti'}`
                : `${visiblePlans.length} ${visiblePlans.length === 1 ? 'piano visualizzato' : 'piani visualizzati'}`}
            </span>
          </div>
          <label className="plans-inactive-filter">
            <span>Nascondi piani inattivi</span>
            <span className="feature-toggle">
              <input
                type="checkbox"
                checked={hideInactivePlans}
                onChange={(event) => setHideInactivePlans(event.target.checked)}
              />
              <span className="feature-toggle-track" aria-hidden="true" />
            </span>
          </label>
        </div>
      )}

      {loading && (
        <div className="plans-loading">
          <span>⏳</span> Caricamento piani…
        </div>
      )}

      {!loading && sortedPlans.length === 0 && (
        <div className="plans-empty">Nessun piano trovato. Creane uno con il pulsante qui sopra.</div>
      )}

      {!loading && sortedPlans.length > 0 && visiblePlans.length === 0 && (
        <div className="plans-empty">
          Nessun piano attivo da mostrare. Disattiva “Nascondi piani inattivi” per visualizzare il catalogo completo.
        </div>
      )}

      {visiblePlans.map((plan) => {
        const isEditing = !!planEditing[plan.id];
        const draft = planDrafts[plan.id] || {};
        const allVersions = plan.versions || [];
        const activeVersion = allVersions.find((v) => v.status === 'PUBLISHED' && v.activeForNewSubscriptions);
        const draftVersions = allVersions.filter((v) => v.status === 'DRAFT');
        const hasDraft = draftVersions.length > 0;
        const historyVersions = allVersions
          .filter((v) => v.status === 'RETIRED' || (v.status === 'PUBLISHED' && !v.activeForNewSubscriptions))
          .sort((a, b) => b.version - a.version);

        return (
          <div className="plan-card" key={plan.id}>
            <div className={`plan-card-header ${!plan.active ? 'inactive' : ''}`}>
              <div className="plan-title-group">
                <span className="plan-code-badge">{plan.code}</span>
                <div>
                  <p className="plan-name">{plan.name}</p>
                  {plan.description && <p style={{ margin: '2px 0 2px', fontSize: 12, color: '#6b7280' }}>{plan.description}</p>}
                  <p className="plan-id">ID: {plan.id}</p>
                  {activeVersion && (
                    <div className="plan-summary">
                      {(activeVersion.prices || []).filter((p) => !p.archivedAt).map((price) => (
                        <span key={price.id} className="plan-summary-chip plan-summary-price">
                          {formatCents(price.amountCents, price.currency)}
                          {price.priceType === 'RECURRING' ? `/${price.interval}` : ' una tantum'}
                        </span>
                      ))}
                      {activeVersion.feePercent != null && (
                        <span className="plan-summary-chip plan-summary-json" title="Fee SoonTip">
                          Fee: {activeVersion.feePercent}% + EUR {((activeVersion.feeFixedCents || 0) / 100).toFixed(2).replace('.', ',')}
                        </span>
                      )}
                      <span className="plan-summary-chip plan-summary-limit" title="Periodo di prova del canone ricorrente">
                        Trial: {(activeVersion.trialDays || 0) > 0 ? `${activeVersion.trialDays} giorni` : 'non previsto'}
                      </span>
                  {activeVersion && featureDefinitions.length > 0 && (() => {
                    const featMap = Object.fromEntries(
                      (activeVersion.features || []).map((f) => [f.featureDefinitionId, f.value])
                    );
                    return featureDefinitions.map((def) => {
                      const vt = (def.valueType || '').toLowerCase();
                      const val = Object.prototype.hasOwnProperty.call(featMap, def.id) ? featMap[def.id] : null;
                      const name = def.name;
                      if (vt === 'boolean') {
                        return (
                          <span key={def.id} className={`plan-summary-chip ${val === true ? 'plan-summary-feature' : 'plan-summary-disabled'}`}>
                            {val === true ? '✓' : '✗'} {name}
                          </span>
                        );
                      }
                      if (vt === 'integer' || vt === 'float') {
                        const isUnlimited = val === null || val === undefined || val === 0;
                        return (
                          <span key={def.id} className="plan-summary-chip plan-summary-limit">
                            {name}: {isUnlimited ? 'illimitato' : val}
                          </span>
                        );
                      }
                      if (vt === 'json' && val && typeof val === 'object') {
                        let summary;
                        if (val.feePercent != null || val.feeFixedCents != null) {
                          const pct = val.feePercent != null ? val.feePercent : 0;
                          const fixed = val.feeFixedCents != null ? (val.feeFixedCents / 100).toFixed(2).replace('.', ',') : '0,00';
                          const cur = val.currency || 'EUR';
                          summary = `${pct}% + ${cur} ${fixed}`;
                        } else {
                          summary = Object.entries(val).map(([k, v]) => `${k}: ${v}`).join(' · ');
                        }
                        return (
                          <span key={def.id} className="plan-summary-chip plan-summary-json" title={summary}>
                            {name}: {summary}
                          </span>
                        );
                      }
                      return (
                        <span key={def.id} className="plan-summary-chip plan-summary-limit">
                          {name}: {val != null ? String(val) : '—'}
                        </span>
                      );
                    });
                  })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="plan-actions">
                <span className={`status-badge ${plan.active ? 'active' : 'inactive'}`}>
                  {plan.active ? 'Attivo' : 'Inattivo'}
                </span>
                <button
                  className="btn-secondary"
                  title="Aggiunge o modifica feature su tutte le versioni del piano"
                  onClick={() => openPlanFeatureEdit(plan, activeVersion)}
                >
                  ⚙️ Gestisci feature
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setPlanEditing((p) => ({ ...p, [plan.id]: !p[plan.id] }));
                    if (!isEditing) {
                      setPlanDrafts((p) => ({
                        ...p,
                        [plan.id]: { name: plan.name, active: plan.active },
                      }));
                    }
                  }}
                >
                  {isEditing ? 'Annulla' : '✏️ Modifica'}
                </button>
                <button
                  className="btn-danger"
                  disabled={saving === `plan-${plan.id}`}
                  title="Elimina piano (solo se nessuna versione ha contratti o abbonamenti)"
                  onClick={() => onDeletePlan(plan)}
                >
                  Elimina
                </button>
              </div>
            </div>

            {isEditing && (
              <div className="plan-edit-row">
                <div className="form-field">
                  <label>Code</label>
                  <input value={plan.code} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
                </div>
                <div className="form-field">
                  <label>Nome</label>
                  <input
                    value={draft.name ?? plan.name}
                    onChange={(e) =>
                      setPlanDrafts((p) => ({
                        ...p,
                        [plan.id]: { ...(p[plan.id] || {}), name: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="form-field">
                  <label>Descrizione <span style={{ fontWeight: 400, color: '#9ca3af' }}>(opzionale)</span></label>
                  <input
                    placeholder="Breve descrizione del piano"
                    value={draft.description ?? plan.description ?? ''}
                    onChange={(e) =>
                      setPlanDrafts((p) => ({
                        ...p,
                        [plan.id]: { ...(p[plan.id] || {}), description: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="form-field" style={{ justifyContent: 'flex-end' }}>
                  <label className="toggle-wrap">
                    <input
                      type="checkbox"
                      checked={draft.active ?? plan.active}
                      onChange={(e) =>
                        setPlanDrafts((p) => ({
                          ...p,
                          [plan.id]: { ...(p[plan.id] || {}), active: e.target.checked },
                        }))
                      }
                    />
                    Attivo
                  </label>
                </div>
                <div className="form-field" style={{ justifyContent: 'flex-end' }}>
                  <button
                    className="btn-primary"
                    onClick={() => onSavePlan(plan)}
                    disabled={saving === `plan-${plan.id}`}
                  >
                    {saving === `plan-${plan.id}` ? 'Salvataggio…' : 'Salva'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Gestisci feature (piano-level, tutte le versioni) ── */}
            {planFeatureEditOpen[plan.id] && featureDefinitions.length > 0 && (() => {
              const draft = planFeatureDrafts[plan.id] || {};
              const saveResult = planFeatureSaveResult[plan.id];
              const isBusy = saving === `plan-features-${plan.id}`;
              const setField = (featureDefinitionId, value) =>
                setPlanFeatureDrafts((p) => ({ ...p, [plan.id]: { ...(p[plan.id] || {}), [featureDefinitionId]: value } }));
              const grouped = featureDefinitions
                .filter((def) => def.key !== 'SOONTIP_FEE')
                .reduce((acc, def) => {
                (acc[def.category] = acc[def.category] || []).push(def);
                return acc;
              }, {});
              return (
                <div style={{ margin: '0 0 0', padding: '14px 20px 16px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                  <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 14, color: '#15803d' }}>⚙️ Gestisci feature — applica a tutte le versioni del piano</p>
                  <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                    ℹ️ Usa questa sezione <strong>solo per feature additive o migliorative</strong> (nuove funzionalità, alzare limiti).<br /><br />
                    Nota importante sul comportamento con i contratti esistenti:<br />
                    • <strong>Feature nuova (non presente in nessuno snapshot esistente)</strong>: il valore viene propagato immediatamente a tutti i subscriber, inclusi quelli con contratto già firmato — lo snapshot non la copre, quindi vince il valore base aggiornato. ✅ Questo è il caso "aggiungono qualcosa in più".<br />
                    • <strong>Feature già presente nello snapshot al momento della firma</strong>: la modifica qui cambia solo il valore base per i nuovi iscritti. Chi ha già firmato continua a vedere il valore fotografato nello snapshot — lo snapshot vince sempre sul base. ⚠️ Quindi se una feature era già inclusa nel contratto firmato, modificarla qui (anche migliorativamente) non ha effetto su quei subscriber.<br /><br />
                    ⚠️ <strong>Per rimuovere una feature</strong> (peggiorativo): il comportamento dipende dal caso.<br />
                    • Se la feature è <strong>presente nello snapshot</strong> dei contratti firmati → rimuoverla qui non ha effetto su quei subscriber (lo snapshot la protegge). La rimozione vale solo per i nuovi iscritti.<br />
                    • Se la feature è stata aggiunta <strong>dopo</strong> alcune firme (quindi assente in quegli snapshot) → rimuoverla qui la toglie immediatamente anche a quei subscriber, perché non c'è un valore snapshot che faccia da scudo.<br />
                    In entrambi i casi la strada sicura è creare un <strong>nuovo piano ex novo</strong> e disattivare il corrente: il vecchio piano resta completamente frozen (qualsiasi modifica futura su questo pannello non lo tocca) e i nuovi iscritti finiscono sul nuovo piano senza quella feature.<br /><br />
                    🔒 <strong>Perché le modifiche qui non impattano chi ha già firmato?</strong> Al momento della firma di un contratto, le feature attive vengono "fotografate" in un <code>resolvedPlanSnapshot</code> nel <code>PlanContractOffer</code>. Quando il sistema calcola i permessi di un tenant, legge prima le <code>PlanFeature</code> aggiornate (base) e poi sovrascrive con quello snapshot — lo snapshot vince sempre. Quindi disabilitare una feature qui cambia il <em>valore base</em> per i nuovi iscritti, ma chi ha già un contratto SIGNED/EFFECTIVE continua a vedere il valore snapshot fotografato alla firma. Per cambiare le feature di chi ha già firmato serve un amendment esplicito che aggiorni il <code>resolvedPlanSnapshot</code>.<br /><br />
                    ⚠️ La <strong>fee SoonTip</strong> non è modificabile qui — è una condizione economica contrattuale. Usa <strong>"💰 Modifica condizioni economiche"</strong>: verrà creata una nuova versione del piano, le condizioni precedenti restano frozen per chi ha già firmato e le nuove si applicano solo ai nuovi sottoscrittori.
                  </p>
                  {Object.entries(grouped).map(([category, defs]) => (
                    <div key={category} className="feature-category">
                      <p className="feature-category-label">{category}</p>
                      {defs.map((def) => {
                        const raw = draft[def.id];
                        const vt = (def.valueType || '').toLowerCase();
                        const jsonProps = vt === 'json' && def.validationSchema?.properties
                          ? Object.entries(def.validationSchema.properties)
                          : null;
                        return (
                          <div key={def.id} className={`feature-row${jsonProps ? ' feature-row-json' : ''}`}>
                            <label className="feature-label" title={def.description || ''}>{def.name}</label>
                            {vt === 'boolean' ? (
                              <label className="feature-toggle">
                                <input type="checkbox" checked={raw === true || raw === 'true' || raw === 1} onChange={(e) => setField(def.id, e.target.checked)} />
                                <span className="feature-toggle-track" />
                              </label>
                            ) : vt === 'integer' ? (
                              <div className="feature-input-wrap">
                                <input type="number" step="1" min="0" className="feature-input" value={raw ?? ''} placeholder="Illimitato" onChange={(e) => setField(def.id, e.target.value === '' ? null : parseInt(e.target.value, 10))} />
                                <NumericLimitHint value={raw} />
                              </div>
                            ) : vt === 'float' ? (
                              <div className="feature-input-wrap">
                                <input type="number" step="0.01" min="0" className="feature-input" value={raw ?? ''} placeholder="Illimitato" onChange={(e) => setField(def.id, e.target.value === '' ? null : parseFloat(e.target.value))} />
                                <NumericLimitHint value={raw} />
                              </div>
                            ) : jsonProps ? (
                              <div className="json-fields">
                                {jsonProps.map(([propKey, propSchema]) => {
                                  const obj = (raw && typeof raw === 'object') ? raw : {};
                                  const propDefault = propSchema.default ?? null;
                                  const propVal = obj[propKey] ?? propDefault ?? '';
                                  const isNum = propSchema.type === 'number' || propSchema.type === 'integer';
                                  const isPercent = isNum && propSchema.maximum != null && propSchema.maximum <= 100 && (propSchema.minimum ?? 0) === 0;
                                  const numVal = propVal === '' ? 0 : Number(propVal);
                                  const update = (v) => setField(def.id, { ...obj, [propKey]: v });
                                  return (
                                    <div key={propKey} className="json-field-row">
                                      <span className="json-field-key">{propKey}</span>
                                      {isPercent ? (
                                        <div className="percent-field">
                                          <input type="range" min={propSchema.minimum ?? 0} max={propSchema.maximum} step="1" value={numVal} onChange={(e) => update(parseFloat(e.target.value))} />
                                          <input type="number" min={propSchema.minimum ?? 0} max={propSchema.maximum} step="1" className="feature-input percent-number" value={propVal === '' ? '' : propVal} placeholder="0" onChange={(e) => update(e.target.value === '' ? 0 : parseFloat(e.target.value))} />
                                          <span className="percent-symbol">%</span>
                                        </div>
                                      ) : (
                                        <input type={isNum ? 'number' : 'text'} step={propSchema.type === 'integer' ? '1' : propSchema.type === 'number' ? '0.01' : undefined} min={propSchema.minimum ?? (isNum ? '0' : undefined)} max={propSchema.maximum} className="feature-input" value={propVal} placeholder={propDefault != null ? String(propDefault) : (isNum ? '0' : '—')} onChange={(e) => { const v = isNum ? (e.target.value === '' ? (propDefault ?? 0) : propSchema.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value)) : e.target.value || (propDefault ?? null); update(v); }} />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <input type="text" className="feature-input" value={raw ?? ''} placeholder="—" onChange={(e) => setField(def.id, e.target.value || null)} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                    <button className="btn-primary" disabled={isBusy} onClick={() => onSavePlanFeatures(plan.id)}>
                      {isBusy ? 'Salvataggio…' : '💾 Salva su tutte le versioni'}
                    </button>
                    {saveResult != null && (
                      <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                        ✓ Feature aggiornate su {saveResult} versioni
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="plan-body">
              {/* ── In produzione ── */}
              <div className="version-section-active">
                <div className="version-section-header">
                  <span className="section-label-active">🟢 In produzione</span>
                  {activeVersion ? (
                    <span className="version-meta">
                      v{activeVersion.version} · dal {formatDate(activeVersion.publishedAt)}
                    </span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: 13 }}>Nessuna versione attiva</span>
                  )}
                  {!hasDraft && (
                    <button
                      className="btn-secondary"
                      disabled={saving === `start-edit-${plan.id}`}
                      onClick={() => onStartEdit(plan.id)}
                    >
                      {saving === `start-edit-${plan.id}` ? '…' : '💰 Modifica condizioni economiche'}
                    </button>
                  )}
                </div>
                {activeVersion && (activeVersion.prices || []).filter((p) => !p.archivedAt).length > 0 ? (
                  <div className="price-summary-list">
                    {(() => {
                      const activePrices = (activeVersion.prices || []).filter((p) => !p.archivedAt);
                      // group by stripeProductId so each product gets a header
                      const groups = [];
                      const seen = {};
                      activePrices.forEach((price) => {
                        const mapping = (price.stripeMappings || []).find((m) => m.stripeProductId);
                        const pid = mapping?.stripeProductId || '__none__';
                        if (!seen[pid]) { seen[pid] = true; groups.push({ pid, prices: [] }); }
                        groups.find((g) => g.pid === pid).prices.push(price);
                      });
                      return groups.map(({ pid, prices: gPrices }) => (
                        <div key={pid}>
                          {pid !== '__none__' && stripeProductNames[pid] && (
                            <div style={{ fontSize: 13, color: '#7c3aed', marginBottom: 6, marginTop: 6 }}>
                              <span style={{ fontWeight: 400, opacity: 0.7 }}>Mappato con prodotto Stripe: </span>
                              <span style={{ fontWeight: 700 }}>{stripeProductNames[pid]}</span>
                            </div>
                          )}
                          {gPrices.map((price) => (
                            <div key={price.id} className="price-summary-row">
                              <strong>{price.purpose}</strong>
                              <span>{formatCents(price.amountCents, price.currency)}</span>
                              <span style={{ color: '#6b7280', fontSize: 12 }}>
                                {price.priceType === 'RECURRING'
                                  ? `ogni ${price.intervalCount || 1} ${price.interval || 'mese'}`
                                  : 'una tantum'}
                              </span>
                              {(price.stripeMappings || []).some((m) => m.verificationStatus === 'VERIFIED') ? (
                                <span className="status-badge published">✓ Stripe OK</span>
                              ) : (
                                <span className="status-badge retired">⚠ Mapping mancante</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                ) : !activeVersion && !hasDraft ? (
                  <p style={{ color: '#9ca3af', fontSize: 13, margin: '8px 0 0' }}>
                    Clicca &ldquo;💰 Modifica condizioni economiche&rdquo; per configurare la prima versione.
                  </p>
                ) : null}
              </div>

              {/* ── Modifiche in bozza ── */}
              {draftVersions.map((version) => {
                    const expanded = !!expandedVersions[version.id];
                    const isBusy = saving.startsWith(`version-${version.id}`) || saving.startsWith('price-');
                    const showArchived = !!showArchivedPrices[version.id];
                    const allPrices = version.prices || [];
                    const archivedCount = allPrices.filter((p) => p.archivedAt).length;
                    const visiblePrices = showArchived ? allPrices : allPrices.filter((p) => !p.archivedAt);
                    const productGroups = Object.values(visiblePrices.reduce((groups, price) => {
                      const mapping = (price.stripeMappings || []).find((item) => !item.archivedAt) || price.stripeMappings?.[0];
                      const productId = mapping?.stripeProductId || `unmapped:${price.id}`;
                      if (!groups[productId]) groups[productId] = { productId: mapping?.stripeProductId || null, prices: [] };
                      groups[productId].prices.push(price);
                      return groups;
                    }, {})).sort((left, right) => {
                      const leftActivationOnly = left.prices.every((price) => price.purpose === 'ACTIVATION');
                      const rightActivationOnly = right.prices.every((price) => price.purpose === 'ACTIVATION');
                      return Number(leftActivationOnly) - Number(rightActivationOnly);
                    });
                    const pickerState = productPickerOpen[version.id] || null;
                    const versionOperationError = versionOperationErrors[version.id] || '';

                    // Compare DRAFT vs active version to decide if publish is meaningful
                    const hasChanges = !activeVersion || (() => {
                      const pKey = (prices) =>
                        (prices || []).filter((p) => !p.archivedAt)
                          .map((p) => {
                            const mappings = (p.stripeMappings || [])
                              .filter((mapping) => !mapping.archivedAt)
                              .map((mapping) => `${mapping.environment}:${mapping.stripeProductId}:${mapping.stripePriceId}`)
                              .sort()
                              .join(',');
                            return `${p.purpose}:${p.amountCents}:${p.currency}:${p.interval ?? ''}:${p.intervalCount ?? ''}:${mappings}`;
                          })
                          .sort().join('|');
                      if (pKey(allPrices) !== pKey(activeVersion.prices)) return true;
                      if ((version.feePercent ?? 20) !== (activeVersion.feePercent ?? 20)) return true;
                      if ((version.feeFixedCents ?? 0) !== (activeVersion.feeFixedCents ?? 0)) return true;
                      if ((version.trialDays ?? 0) !== (activeVersion.trialDays ?? 0)) return true;
                      const fKey = (features) =>
                        (features || [])
                          .map((f) => `${f.featureDefinitionId}:${JSON.stringify(f.value)}`)
                          .sort().join('|');
                      return fKey(version.features) !== fKey(activeVersion.features);
                    })();

                    return (
                      <div className="version-item version-item-draft" key={version.id}>
                        <div className="version-header" onClick={() => toggleVersionExpand(version.id)}>
                          <div className="version-header-left">
                            <span className="section-label-draft">📝 Modifiche in bozza</span>
                            <span className="version-num">v{version.version}</span>
                          </div>
                          <div className="version-actions-bar" onClick={(e) => e.stopPropagation()}>
                            <button
                              className={hasChanges ? 'btn-primary' : 'btn-ghost'}
                              disabled={isBusy || !hasChanges}
                              title={hasChanges ? 'Pubblica e rendi attiva per i nuovi iscritti' : 'Nessuna modifica rispetto alla versione in produzione'}
                              onClick={() => onUpdateVersion(version.id, { status: 'PUBLISHED', activeForNewSubscriptions: true })}
                            >
                              {saving === `version-${version.id}-status` ? '…' : hasChanges ? '🚀 Pubblica e attiva' : '✓ Nessuna modifica'}
                            </button>
                            <button
                              className="btn-danger"
                              disabled={isBusy}
                              title="Scarta questa bozza"
                              onClick={() => onDeleteVersion(version.id)}
                            >
                              Scarta bozza
                            </button>
                            <span style={{ color: '#d1d5db', fontSize: 18 }}>{expanded ? '▲' : '▼'}</span>
                          </div>
                        </div>

                        {expanded && (
                          <div className="version-body">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <p className="prices-section-title" style={{ margin: 0 }}>Prezzi</p>
                              {archivedCount > 0 && (
                                <button
                                  className="btn-sm-text"
                                  onClick={() => setShowArchivedPrices((p) => ({ ...p, [version.id]: !p[version.id] }))}
                                >
                                  {showArchived ? `Nascondi archiviati (${archivedCount})` : `Mostra archiviati (${archivedCount})`}
                                </button>
                              )}
                            </div>

                            {versionOperationError && !pickerState && (
                              <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, padding: '9px 11px', border: '1px solid #fecaca', borderRadius: 7, background: '#fef2f2', color: '#b91c1c', fontSize: 12 }}>
                                <span style={{ flex: 1 }}>{versionOperationError}</span>
                                <button
                                  type="button"
                                  className="btn-sm-text"
                                  style={{ color: '#b91c1c', flexShrink: 0 }}
                                  onClick={() => setVersionOperationErrors((previous) => ({ ...previous, [version.id]: '' }))}
                                >
                                  Chiudi
                                </button>
                              </div>
                            )}

                            {allPrices.length === 0 ? (
                              <div className="prices-empty">Nessun prezzo configurato.</div>
                            ) : visiblePrices.length === 0 ? (
                              <div className="prices-empty">Tutti i prezzi sono archiviati.</div>
                            ) : (
                              <div className="stripe-product-groups">
                                {productGroups.map((group) => (
                                  <StripeProductGroup
                                    key={group.productId || group.prices[0].id}
                                    group={group}
                                    productName={group.productId ? stripeProductNames[group.productId] : null}
                                    saving={saving}
                                    mappingFormOpen={mappingFormOpen}
                                    mappingDrafts={mappingDrafts}
                                    onTogglePrice={onTogglePrice}
                                    onVerifyMapping={onVerifyMapping}
                                    onDeleteMapping={onDeleteMapping}
                                    openMappingForm={openMappingForm}
                                    setMappingDrafts={setMappingDrafts}
                                    onCreateMapping={onCreateMapping}
                                    setMappingFormOpen={setMappingFormOpen}
                                    onSync={() => onImportFromProduct(version.id, group.productId, 'sync')}
                                    onReplace={() => {
                                      setVersionOperationErrors((previous) => ({ ...previous, [version.id]: '' }));
                                      setProductPickerOpen((previous) => ({
                                        ...previous,
                                        [version.id]: {
                                          mode: 'replace-role',
                                          productId: group.productId,
                                          targetRoles: Array.from(new Set(group.prices.map((price) => price.purpose))),
                                          selectedPriceIds: [],
                                          selectedPrices: [],
                                          selectionError: '',
                                        },
                                      }));
                                    }}
                                  />
                                ))}
                              </div>
                            )}

                            {/* ── Product catalog actions ── */}
                            <div className="product-picker-section">
                              {pickerState ? (
                                <>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
                                    {pickerState.mode === 'replace-role'
                                      ? 'Seleziona il prodotto sostitutivo. Verranno archiviate soltanto le condizioni dello stesso ruolo economico.'
                                      : 'Seleziona un nuovo prodotto. I prodotti già configurati resteranno invariati.'}
                                  </p>
                                  <StripeProductPriceSelector
                                    mode="products"
                                    selectedPriceIds={pickerState.selectedPriceIds || []}
                                    onPriceChange={(priceIds, prices) => {
                                      setVersionOperationErrors((previous) => ({ ...previous, [version.id]: '' }));
                                      const productIds = Array.from(new Set(prices.map((price) => price.stripeProductId)));
                                      const selectedRoles = Array.from(new Set(prices.map((price) => price.recurring ? 'SUBSCRIPTION' : 'ACTIVATION')));
                                      const targetRoles = pickerState.targetRoles || [];
                                      let selectionError = '';
                                      if (productIds.length > 1) {
                                        selectionError = 'Seleziona Price appartenenti a un solo Product Stripe per operazione.';
                                      } else if (pickerState.mode === 'replace-role') {
                                        const sameRoles = targetRoles.length === selectedRoles.length
                                          && targetRoles.every((role) => selectedRoles.includes(role));
                                        if (!sameRoles) {
                                          selectionError = `La selezione deve coprire i ruoli: ${targetRoles.join(', ')}.`;
                                        }
                                      }
                                      setProductPickerOpen((previous) => ({
                                        ...previous,
                                        [version.id]: {
                                          ...previous[version.id],
                                          selectedPriceIds: priceIds,
                                          selectedPrices: prices,
                                          selectionError,
                                        },
                                      }));
                                    }}
                                  />
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                    <button
                                      className="btn-primary"
                                      style={{ fontSize: 12, flexShrink: 0 }}
                                      disabled={!(pickerState.selectedPriceIds || []).length || Boolean(pickerState.selectionError)}
                                      onClick={() => {
                                        const productId = pickerState.selectedPrices?.[0]?.stripeProductId;
                                        if (!productId) return;
                                        onImportFromProduct(
                                          version.id,
                                          productId,
                                          pickerState.mode,
                                          pickerState.targetRoles || [],
                                          pickerState.selectedPriceIds || []
                                        );
                                      }}
                                    >
                                      Applica selezione
                                    </button>
                                    <button
                                      className="btn-ghost"
                                      style={{ fontSize: 12, flexShrink: 0 }}
                                      onClick={() => setProductPickerOpen((p) => ({ ...p, [version.id]: false }))}
                                    >
                                      Annulla
                                    </button>
                                    {(pickerState.selectionError || versionOperationError) && (
                                      <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 280px', minWidth: 0, padding: '7px 9px', border: '1px solid #fecaca', borderRadius: 7, background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 600 }}>
                                        <span style={{ flex: 1, minWidth: 0 }}>{pickerState.selectionError || versionOperationError}</span>
                                        {versionOperationError && !pickerState.selectionError && (
                                          <button
                                            type="button"
                                            className="btn-sm-text"
                                            style={{ color: '#b91c1c', flexShrink: 0 }}
                                            onClick={() => setVersionOperationErrors((previous) => ({ ...previous, [version.id]: '' }))}
                                          >
                                            Chiudi
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <button
                                  className="btn-primary"
                                  disabled={saving === `import-product-${version.id}`}
                                  onClick={() => {
                                    setVersionOperationErrors((previous) => ({ ...previous, [version.id]: '' }));
                                    setProductPickerOpen((previous) => ({
                                      ...previous,
                                      [version.id]: {
                                        mode: 'add',
                                        selectedPriceIds: [],
                                        selectedPrices: [],
                                        selectionError: '',
                                      },
                                    }));
                                  }}
                                >
                                  {saving === `import-product-${version.id}` ? 'Importazione…' : '+ Aggiungi prodotto Stripe'}
                                </button>
                              )}
                            </div>

                            {/* ── Fee SoonTip (condizione economica prima-classe) ── */}
                            {(() => {
                              const feeDraft = versionFeeDrafts[version.id] ?? {
                                feePercent: version.feePercent ?? 20,
                                feeFixedCents: version.feeFixedCents ?? 0,
                                trialDays: version.trialDays ?? 0,
                              };
                              const setFeeField = (field, val) =>
                                setVersionFeeDrafts((prev) => ({
                                  ...prev,
                                  [version.id]: { ...(prev[version.id] ?? { feePercent: version.feePercent ?? 20, feeFixedCents: version.feeFixedCents ?? 0, trialDays: version.trialDays ?? 0 }), [field]: val },
                                }));
                              return (
                                <div className="features-section">
                                  <div style={{ marginBottom: 12, padding: '8px 12px', background: '#eff6ff', borderRadius: 6, fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
                                    ℹ️ In questa bozza puoi modificare <strong>fee SoonTip e periodo di prova</strong> (condizioni economiche) — al salvataggio verrà creata una <strong>nuova versione</strong> del piano.
                                    {' '}I subscriber esistenti resteranno sulla versione attuale con tutte le loro condizioni economiche frozen (prezzi, fee, termini contrattuali); le nuove condizioni si applicheranno <strong>solo ai nuovi sottoscrittori</strong> che firmano dopo la pubblicazione.<br /><br />
                                    <strong>Per aggiungere o migliorare feature</strong> (migliorativo ✅) usa <strong>⚙️ Gestisci feature</strong>: aggiungere funzionalità o alzare limiti è sempre ben visto da chi ha già sottoscritto — ricevono qualcosa in più senza aver chiesto nulla.<br /><br />
                                    <strong>Per rimuovere una feature</strong> (peggiorativo ⚠️) crea un <strong>nuovo piano ex novo</strong> e disattiva quello corrente.
                                  </div>
                                  <div className="economic-subsection">
                                    <p className="prices-section-title" style={{ marginTop: 16, marginBottom: 10 }}>Condizioni economiche — Fee SoonTip</p>
                                    <div className="feature-category">
                                    <div className="feature-row">
                                      <label className="feature-label">Fee % SoonTip</label>
                                      <div className="feature-input-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input
                                          type="range"
                                          min="0"
                                          max="100"
                                          step="1"
                                          value={feeDraft.feePercent}
                                          onChange={(e) => setFeeField('feePercent', parseInt(e.target.value, 10))}
                                          style={{ width: 120 }}
                                        />
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          step="1"
                                          className="feature-input percent-number"
                                          value={feeDraft.feePercent}
                                          onChange={(e) => setFeeField('feePercent', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                                          style={{ width: 60 }}
                                        />
                                        <span className="percent-symbol">%</span>
                                      </div>
                                    </div>
                                    <div className="feature-row">
                                      <label className="feature-label">Fee fissa (centesimi €)</label>
                                      <div className="feature-input-wrap">
                                        <input
                                          type="number"
                                          min="0"
                                          step="1"
                                          className="feature-input"
                                          value={feeDraft.feeFixedCents}
                                          placeholder="0"
                                          onChange={(e) => setFeeField('feeFixedCents', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                                        />
                                        <span style={{ marginLeft: 4, fontSize: 12, color: '#6b7280' }}>
                                          = EUR {((feeDraft.feeFixedCents || 0) / 100).toFixed(2).replace('.', ',')}
                                        </span>
                                      </div>
                                    </div>
                                    </div>
                                  </div>
                                  <div className="economic-subsection economic-subsection-separated">
                                    <p className="prices-section-title" style={{ marginBottom: 10 }}>Condizioni economiche — Periodo di prova</p>
                                    <div className="feature-category">
                                    <div className="feature-row">
                                      <label className="feature-label">Prova gratuita (giorni)</label>
                                      <div className="feature-input-wrap">
                                        <input
                                          type="number"
                                          min="0"
                                          max="365"
                                          step="1"
                                          className="feature-input"
                                          value={feeDraft.trialDays}
                                          onChange={(e) => setFeeField('trialDays', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                                        />
                                        <span style={{ marginLeft: 4, fontSize: 12, color: '#6b7280' }}>0 = nessuna prova</span>
                                      </div>
                                    </div>
                                    </div>
                                  </div>
                                  <button
                                    className="btn-secondary"
                                    style={{ marginTop: 10 }}
                                    disabled={saving === `fee-${version.id}`}
                                    onClick={() => withSaving(`fee-${version.id}`, async () => {
                                      await planService.updateVersion(version.id, { feePercent: feeDraft.feePercent, feeFixedCents: feeDraft.feeFixedCents, trialDays: feeDraft.trialDays });
                                      setVersionFeeDrafts((prev) => { const n = { ...prev }; delete n[version.id]; return n; });
                                    })}
                                  >
                                    {saving === `fee-${version.id}` ? 'Salvataggio…' : '💾 Salva condizioni economiche'}
                                  </button>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })
              }

              {/* ── Storico ── */}
              {historyVersions.length > 0 && (
                <details className="version-history-section">
                  <summary>
                    📋 Storico ({historyVersions.length} {historyVersions.length === 1 ? 'versione precedente' : 'versioni precedenti'})
                  </summary>
                  {historyVersions.map((version) => {
                    const isExpanded = expandedHistoryVersion === version.id;
                    const featMap = Object.fromEntries(
                      (version.features || []).map((f) => [f.featureDefinitionId, f.value])
                    );
                    return (
                    <div key={version.id} className="history-version-row" style={{ flexWrap: 'wrap' }}>
                      <span className="version-num">v{version.version}</span>
                      <span className={`status-badge ${VERSION_STATUS_CLASS[version.status] || 'draft'}`}>
                        {VERSION_STATUS_LABEL[version.status] || version.status}
                      </span>
                      <span className="version-meta">
                        {version.publishedAt && `Pubblicata ${formatDate(version.publishedAt)}`}
                        {version.retiredAt && ` · Ritirata ${formatDate(version.retiredAt)}`}
                      </span>
                      {featureDefinitions.length > 0 && (
                        <button
                          className="btn-sm"
                          style={{ marginLeft: 'auto', fontSize: 11 }}
                          onClick={() => setExpandedHistoryVersion(isExpanded ? null : version.id)}
                        >
                          {isExpanded ? '▲ Nascondi' : '▼ Dettagli'}
                        </button>
                      )}
                      {isExpanded && (
                        <div style={{ width: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                          {Object.entries(
                            featureDefinitions.reduce((acc, def) => { (acc[def.category] = acc[def.category] || []).push(def); return acc; }, {})
                          ).map(([category, defs]) => (
                            <div key={category} style={{ marginBottom: 10 }}>
                              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{category}</p>
                              {defs.map((def) => {
                                const val = Object.prototype.hasOwnProperty.call(featMap, def.id) ? featMap[def.id] : null;
                                const vt = (def.valueType || '').toLowerCase();
                                let display;
                                if (vt === 'boolean') {
                                  display = (val === true || val === 'true' || val === 1)
                                    ? <span style={{ color: '#059669', fontWeight: 600 }}>✓ Abilitata</span>
                                    : <span style={{ color: '#9ca3af' }}>✗ Disabilitata</span>;
                                } else if (vt === 'json' && val && typeof val === 'object') {
                                  display = <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#374151' }}>{JSON.stringify(val)}</span>;
                                } else {
                                  display = val === null || val === undefined
                                    ? <span style={{ color: '#9ca3af' }}>illimitato / non impostato</span>
                                    : <span style={{ fontWeight: 600 }}>{String(val)}</span>;
                                }
                                return (
                                  <div key={def.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', fontSize: 12, borderBottom: '1px solid #f3f4f6' }}>
                                    <span style={{ color: '#374151' }}>{def.name}</span>
                                    {display}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </details>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Plans;
